import React, { FC, useState, useMemo, useEffect } from 'react';
import { Slider } from 'antd';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import './MovementInsightsStep.scss';

interface MovementInsightsStepProps {
  insightIndex: number;
  values?: string[];
  onChange?: (vals: string[]) => void;
}

// Movement Insights 1/5 to 5/5
// 'Mobility level| 'Stability Control| 'Balance Consistency | 'Movement Symmetry' | 'Overall Movement Quality'
export const MovementInsightsStep: FC<MovementInsightsStepProps> = ({
  insightIndex,
  values: propValues,
  onChange,
}) => {
  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const movementInsightsData = programMasterData?.movementInsights;

  const items = movementInsightsData?.items;
  // Use the API options and ensure they are sorted by order
  const insights = useMemo(() => {
    return [...(items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [items]);

  const commonOptions = movementInsightsData?.commonOptions;
  const options = useMemo(() => {
    return [...(commonOptions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [commonOptions]);

  // Store values for all insights independently, defaulting to 1 (Pain)
  // We use a safe length from insights or fallback to 5
  const maxInsights = insights.length > 0 ? insights.length : 5;
  const [localValues, setLocalValues] = useState<string[]>(Array(maxInsights).fill('pain'));
  const values = propValues !== undefined ? propValues : localValues;

  useEffect(() => {
    if (propValues === undefined && onChange) {
      onChange(Array(maxInsights).fill('pain'));
    }
  }, [propValues, onChange, maxInsights]);

  // Safe fallback if insightIndex goes out of bounds
  const currentInsightIndex = Math.min(Math.max(insightIndex - 1, 0), insights.length - 1);
  const currentInsight = insights[currentInsightIndex];

  const currentValueStr = values[currentInsightIndex] || 'pain';
  const currentValueIndex = Math.max(
    1,
    options.findIndex((opt) => opt.slug === currentValueStr) + 1,
  );

  const handleValueChange = (valIndex: number) => {
    const newValues = [...values];
    const option = options[valIndex - 1];
    if (option) {
      newValues[currentInsightIndex] = option.slug;
      if (onChange) onChange(newValues);
      setLocalValues(newValues);
    }
  };

  // If headerTitle is provided, e.g. "Movement Insights 1/5", use it.
  const displayHeader = currentInsight?.headerTitle;

  if (isLoading) {
    return <div className="movement-insights-step">{'Loading options...'}</div>;
  }

  if (isError || !movementInsightsData) {
    return <div className="movement-insights-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="movement-insights-step">
      <h2 className="step-title">
        {displayHeader.replace(/\d+\/\d+/, '')}
        <span className="step-total">{`${insightIndex}/${insights.length}`}</span>
      </h2>

      <div className="movement-card">
        <div className="movement-card-header">
          <h3 className="movement-title">{currentInsight?.title}</h3>
          <p className="movement-description">{currentInsight?.subtitle}</p>
        </div>

        <div className="slider-container">
          <Slider
            min={1}
            max={options.length}
            step={1}
            dots={true}
            value={currentValueIndex}
            onChange={(val) => handleValueChange(val as number)}
            tooltip={{ open: false }}
            className="movement-slider"
          />
        </div>

        <div className="insight-labels">
          {options.map((opt, index) => {
            const optValue = index + 1;
            return (
              <div
                key={opt.id}
                className={`insight-label-item ${currentValueIndex === optValue ? 'active' : ''}`}
                onClick={() => handleValueChange(optValue)}
              >
                <span className="label-text">{opt.title}</span>
                {/* <span className="info-icon">{<InfoIcon />}</span> */}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
